<template>
  <div class="h-full">
    <textarea
      id="notes-textbox"
      ref="notes-student"
      placeholder="Take notes before and during the discussion."
      @keyup="queueSaving"
      type="text"
      v-model="notesContent"
      class="text-sm border relative h-60 border-coolGray-200 text-gray-500 outline-none px-4 py-3 w-full rounded"
    ></textarea>
    <div>
      <div class="flex justify-end">
        <button
          ref="btnRef"
          v-on:click="togglePopover()"
          @click="show_stems = true"
          class="px-4 pt-2 pb-1 hover:bg-gray-100 rounded cursor-pointer text-gray-400 font-semibold flex items-center justify-center outline-none"
        >
          <img src="/static/ld-sentence-stems.svg" class="px-1" />
          Stems
        </button>
        <div
          ref="popoverRef"
          v-click-outside="{
            exclude: ['btnRef'],
            handler: 'closeStems',
          }"
          v-bind:class="{ hidden: !popoverShow, block: popoverShow }"
          class="bg-white w-max max-w-lg border mr-3 block z-50 font-normal leading-normal text-sm text-left no-underline break-words rounded-lg h-96 overflow-y-auto"
        >
          <div>
            <div class="p-3">
              <div class="text-left overflow-y-scroll">
                <div class="my-3">
                  <p class="text-left font-semibold text-primary">
                    <i class="fas fa-play mr-1"></i> {{ $t('new_idea') }}
                  </p>
                  <ul
                    class="py-1"
                    v-for="(sentence, index) in default_new_idea_sentences"
                    :key="index"
                  >
                    <li
                      v-if="sentence.is_active"
                      class="flex center pl-0 hover:bg-gray-300 cursor-pointer"
                    >
                      <span
                        v-if="sentence.is_custom"
                        class="text-sm leading-6"
                        @click="addStemToNotes(sentence.sentence)"
                      >
                        {{ sentence.sentence }}</span
                      >
                      <span
                        v-else
                        class="text-sm leading-6"
                        @click="addStemToNotes($t('stem_new_' + index))"
                      >
                        {{ $t('stem_new_' + index) }}
                      </span>
                    </li>
                  </ul>
                </div>
                <div class="my-3">
                  <p class="text-left font-semibold text-green-700">
                    <i class="fas fa-square mr-1.5"></i>{{ $t('build_on') }}
                  </p>
                  <ul
                    class="py-1"
                    v-for="(sentence, index) in default_build_on_sentences"
                    :key="index"
                  >
                    <li
                      v-if="sentence.is_active"
                      class="flex center pl-0 hover:bg-gray-300 cursor-pointer"
                    >
                      <span
                        v-if="sentence.is_custom"
                        class="text-sm leading-6"
                        @click="addStemToNotes(sentence.sentence)"
                      >
                        {{ sentence.sentence }}</span
                      >
                      <span
                        v-else
                        class="text-sm leading-6"
                        @click="addStemToNotes($t('stem_build_' + index))"
                      >
                        {{ $t('stem_build_' + index) }}
                      </span>
                    </li>
                  </ul>
                </div>
                <div class="my-3">
                  <p class="text-left font-semibold text-pinky">
                    <i class="fas fa-circle mr-1.5"></i>{{ $t('challenge') }}
                  </p>
                  <ul
                    class="py-1"
                    v-for="(sentence, index) in default_challenge_sentences"
                    :key="index"
                  >
                    <li
                      v-if="sentence.is_active"
                      class="flex center pl-0 hover:bg-gray-300 cursor-pointer"
                    >
                      <span
                        v-if="sentence.is_custom"
                        class="text-sm leading-6"
                        @click="addStemToNotes(sentence.sentence)"
                      >
                        {{ sentence.sentence }}</span
                      >
                      <span
                        v-else
                        class="text-sm leading-6"
                        @click="addStemToNotes($t('stem_challenge_' + index))"
                      >
                        {{ $t('stem_challenge_' + index) }}
                      </span>
                    </li>
                  </ul>
                </div>
                <div class="my-3">
                  <p class="text-left font-semibold text-darkBlue-dark flex">
                    <img
                      class="-ml-0.5 mr-1 h-5 w-5"
                      src="/static/live_discussion/ld_question_icon.svg"
                    />
                    {{ $t('question') }}
                  </p>
                  <ul
                    class="py-1"
                    v-for="(sentence, index) in default_question_sentences"
                    :key="index"
                  >
                    <li
                      v-if="sentence.is_active"
                      class="flex center pl-0 hover:bg-gray-300 cursor-pointer"
                    >
                      <span
                        v-if="sentence.is_custom"
                        class="text-sm leading-6"
                        @click="addStemToNotes(sentence.sentence)"
                      >
                        {{ sentence.sentence }}</span
                      >
                      <span
                        v-else
                        class="text-sm leading-6"
                        @click="addStemToNotes($t('stem_question_' + index))"
                      >
                        {{ $t('stem_question_' + index) }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue';
  import store from 'store';
  import { mapGetters } from 'vuex';
  import { createPopper } from '@popperjs/core';
  // Services
  import Edit_Student_Notes from 'services/liveDiscussion/editStudentNotes';
  import debounce from 'lodash/debounce';

  let handleOutsideClick;
  Vue.directive('click-outside', {
    bind(el, binding, vnode) {
      // Here's the click/touchstart handler
      // (it is registered below)
      handleOutsideClick = (e) => {
        e.stopPropagation();
        // Get the handler method name and the exclude array
        // from the object used in v-click-outside
        const { handler, exclude } = binding.value;
        // This variable indicates if the clicked element is excluded
        let clickedOnExcludedEl = false;
        exclude.forEach((refName) => {
          // We only run this code if we haven't detected
          // any excluded element yet
          if (!clickedOnExcludedEl) {
            // Get the element using the reference name
            const excludedEl = vnode.context.$refs[refName];
            // See if this excluded element
            // is the same element the user just clicked on
            clickedOnExcludedEl = excludedEl.contains(e.target);
          }
        });
        // We check to see if the clicked element is not
        // the dialog element and not excluded
        if (!el.contains(e.target) && !clickedOnExcludedEl) {
          // If the clicked element is outside the dialog
          // and not the button, then call the outside-click handler
          // from the same component this directive is used in
          vnode.context[handler]();
        }
      };
      // Register click/touchstart event listeners on the whole page
      document.addEventListener('click', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    },
    unbind() {
      // If the element that has v-click-outside is removed, then
      // unbind click/touchstart listeners from the whole page
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    },
  });

  export default Vue.extend({
    data() {
      return {
        notesContent: '',
        isSaving: false,
        show_saved_message: false,
        show_stems: false,
        popoverShow: false,
      };
    },
    computed: {
      ...mapGetters([
        'hasTeacherPermissionsInCourse',
        'getCurrentDiscussion',
        'getCourseDetail',
        'getCourseLoading',
        'getMyParticipation',
        'getMySubscription',
        'getProfile',
        'getProviders',
        'getStudentSummaryLD',
      ]),
      default_new_idea_sentences() {
        return this.getCurrentDiscussion.new_idea_sentences;
      },
      default_build_on_sentences() {
        return this.getCurrentDiscussion.build_on_sentences;
      },
      default_challenge_sentences() {
        return this.getCurrentDiscussion.challenge_sentences;
      },
      default_question_sentences() {
        return this.getCurrentDiscussion.question_sentences;
      },
    },
    methods: {
      queueSaving() {
        this.show_saved_message = false;
        this.isSaving = true;
        this.debouncedSave();
      },
      handleChangeStudentNotes: function () {
        this.isSaving = true;
        this.show_saved_message = true;
        Edit_Student_Notes(this.getMyParticipation.id, this.notesContent).then(
          () => {
            const updatedNotes = Object.assign(
              {},
              this.getMyParticipation.notes,
              { content: this.notesContent }
            );
            store
              .dispatch('updateParticipationSpread', {
                id: this.getMyParticipation.id,
                notes: updatedNotes,
              })
              .then(() => {
                this.show_saved_message = true;
                this.isSaving = false;
                setTimeout(() => {
                  this.show_saved_message = false;
                }, 2000);
              });
          }
        );
      },
      togglePopover() {
        if (this.popoverShow) {
          this.popoverShow = false;
        } else {
          this.popoverShow = true;
          createPopper(this.$refs.btnRef, this.$refs.popoverRef, {
            placement: 'top-end',
          });
        }
      },
      addStemToNotes(sentence) {
        this.notesContent = this.notesContent.concat('\n' + sentence);
        this.popoverShow = false;
        return Edit_Student_Notes(this.getMyParticipation.id, this.notesContent)
          .then(() => {
            const updatedNotes = Object.assign(
              {},
              this.getMyParticipation.notes,
              { content: this.notesContent }
            );
            store
              .dispatch('updateParticipationSpread', {
                id: this.getMyParticipation.id,
                notes: updatedNotes,
              })
              .then(() => {
                this.show_saved_message = true;
                this.isSaving = false;
                setTimeout(() => {
                  this.show_saved_message = false;
                }, 2000);
              });
          })
          .catch((e) => {
            console.error(e);
          });
      },
      closeStems() {
        this.popoverShow = false;
      },
    },
    mounted() {
      this.$i18n.locale = this.getCurrentDiscussion.language;
      this.notesContent = this.getMyParticipation.notes.content;
      if (this.getMyParticipation.id) {
        this.debouncedSave = debounce(this.handleChangeStudentNotes, 500, {
          leading: false,
          trailing: true,
        });
      }
      const unwatchNotesContent = this.$watch(
        'getMyParticipation.notes',
        (newNotes, oldNotes) => {
          if (!oldNotes.content) {
            this.notesContent = newNotes.content;
          }
          // only want to run once so we unwatch after its first use
          unwatchNotesContent();
        }
      );
    },
    beforeDestroy() {
      if (this.getMyParticipation.id) {
        this.handleChangeStudentNotes();
      }
    },
  });
</script>



// WEBPACK FOOTER //
// StudentMakeNotes.vue?17d0b617